import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { Button, Callout, Spinner, Tab, TabGroup } from '@humanforce/mango-ui-react';
import { PageTitle } from '../components/layout/PageTitle';
import { WorkerStatusTag } from '../features/workers/components/WorkerStatusTag';
import { PersonalTab } from '../features/workers/sections/PersonalTab';
import { getWorker, fullLegalName } from '../features/workers/workersApi';
import type { PersonDetailResponse } from '../features/workers/workersApiTypes';

// The 11 top-level Worker Record tabs (RR-Worker-Profile-V2 / PRD). Only Personal is built in
// PRR-58; the rest are visible "coming soon" placeholders (D-3). The Person-layer data lives as
// sections inside Personal (D-4), not as top-level tabs.
const TABS = [
  { slug: 'overview', label: 'Overview' },
  { slug: 'personal', label: 'Personal' },
  { slug: 'pay', label: 'Pay' },
  { slug: 'tax', label: 'Tax' },
  { slug: 'banking', label: 'Banking' },
  { slug: 'super', label: 'Super' },
  { slug: 'leave', label: 'Leave' },
  { slug: 'employment', label: 'Employment' },
  { slug: 'documents', label: 'Documents' },
  { slug: 'notes', label: 'Notes' },
  { slug: 'activity', label: 'Activity' },
] as const;

const ComingSoon = ({ label }: { label: string }) => (
  <div style={{ paddingTop: 'var(--mgo-space-40)' }}>
    <Callout tone="info" title={`${label} — coming soon`}>
      This part of the worker record is not yet available. It will be delivered in a future ticket.
    </Callout>
  </div>
);

const initials = (person: PersonDetailResponse) =>
  [person.legalFirstName?.[0], person.legalLastName?.[0]].filter(Boolean).join('').toUpperCase() || '—';

export const WorkerRecordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { '*': wildcard } = useParams();
  const personId = searchParams.get('worker') ?? undefined;
  const activeSlug = TABS.some((t) => t.slug === wildcard) ? (wildcard as string) : 'overview';

  const [person, setPerson] = useState<PersonDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (signal?: { ignore: boolean }) => {
    if (!personId) { setLoading(false); setError(null); setPerson(null); return; }
    setLoading(true);
    setError(null);
    const result = await getWorker(personId);
    if (signal?.ignore) return;
    if (result.ok && result.data) setPerson(result.data);
    else setError(result.message ?? 'Failed to load this worker.');
    setLoading(false);
  }, [personId]);

  useEffect(() => {
    const signal = { ignore: false };
    void load(signal);
    return () => { signal.ignore = true; };
  }, [load]);

  if (!personId)
    return (
      <>
        <PageTitle title="Worker record" />
        <Callout tone="caution" title="No worker selected" action={<Button variant="secondary" label="Go to Workers" onClick={() => navigate('/workers/worker-list')} />}>
          Open a worker record from the Workers list.
        </Callout>
      </>
    );

  const readOnly = person?.status === 'terminated';

  return (
    <>
      <PageTitle title="Worker record" actions={<Button variant="tertiary" label="Back to workers" onClick={() => navigate('/workers/worker-list')} />} />

      {error && <Callout tone="critical" title="Couldn't load worker">{error}</Callout>}

      {loading ? (
        <div className="mgo-cluster mgo-gap-s mgo-align-center" style={{ padding: 'var(--mgo-space-60)' }}>
          <Spinner /> <span className="mgo-text-s mgo-color-muted">Loading worker…</span>
        </div>
      ) : person ? (
        <>
          <div className="mgo-cluster mgo-gap-l mgo-align-center mgo-background-raised mgo-border-neutral mgo-radius-strong" style={{ padding: 'var(--mgo-space-40)', marginBottom: 'var(--mgo-space-40)' }}>
            <span aria-hidden className="mgo-background-brand mgo-radius-rounded mgo-weight-strong" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '3rem', height: '3rem', color: 'var(--mgo-color-fg-brand)' }}>
              {initials(person)}
            </span>
            <div className="mgo-stack mgo-gap-2xs" style={{ minWidth: 0 }}>
              <div className="mgo-cluster mgo-gap-s mgo-align-center">
                <span className="mgo-text-l mgo-weight-strong">{fullLegalName(person)}</span>
                <WorkerStatusTag status={person.status ?? 'active'} />
              </div>
              <span className="mgo-text-xs mgo-color-muted">
                {[person.preferredFirstName && `Preferred: ${person.preferredFirstName}`, person.globalEmployeeId && `ID: ${person.globalEmployeeId}`, person.pronouns].filter(Boolean).join(' · ') || '—'}
              </span>
            </div>
          </div>

          <TabGroup value={activeSlug} renderActiveOnly={false} onValueChange={(value) => { if (value !== activeSlug) navigate(`/workers/worker-record/${value}?worker=${personId}`); }}>
            {TABS.map((tab) => (
              <Tab key={tab.slug} value={tab.slug} label={tab.label}>
                {tab.slug === 'personal'
                  ? <PersonalTab person={person} personId={personId} readOnly={Boolean(readOnly)} onPersonUpdated={() => void load()} />
                  : <ComingSoon label={tab.label} />}
              </Tab>
            ))}
          </TabGroup>
        </>
      ) : null}
    </>
  );
};
